import React from 'react';
import { Link, useLocation } from 'react-router-dom';
import { IconArrowRight, IconBrandFacebook, IconBrandWhatsapp, IconMail, IconPhone } from '@tabler/icons-react';
import logo from '../asset/logo.webp';
import '../styles/footer-style.css';
import ThemeToggle from './ThemeToggle';
import { whatsappLink } from '../contactConfig';
import certificationLogos from '../asset/certifications';
import { LANGUAGES, localizePath, stripLang, useContent, useLang } from '../i18n';

// Product links jump to sections of the Products page (ids set in Products.js)
const PRODUCT_ANCHORS = ['continental', 'hamburg', 'pieces', 'other-products'];
const COMPANY_PATHS = ['/uses', '/how-we-work', '/true-cinnamon', '/contact'];
// These pages already end with their own quote call to action
const PAGES_WITHOUT_CTA = ['/', '/how-we-work', '/contact'];

const OFFICES = [
  { key: 'sriLanka', flag: '🇱🇰', address: '264/A, Regiland Estate, Batapola', phone: '+94 71 721 3161', tel: '+94717213161' },
  { key: 'singapore', flag: '🇸🇬', address: '6 Telok Blangah Crescent, #02-01, Singapore', phone: '+65 8748 4904', tel: '+6587484904' },
];

function Footer({ theme, toggleTheme }) {
  const t = useContent('common');
  const lang = useLang();
  const path = stripLang(useLocation().pathname);
  const lp = (p) => localizePath(p, lang);
  const otherLang = lang === 'en' ? 'es' : 'en';
  const f = t.footer;

  return (
    <footer className="site-footer">
      {!PAGES_WITHOUT_CTA.includes(path) && (
        <section className="fo-cta">
          <div className="container fo-cta-inner">
            <div>
              <h2 className="fo-cta-title">{f.ctaTitle}</h2>
              <p className="fo-cta-text">{f.ctaText}</p>
            </div>
            <div className="fo-cta-actions">
              <Link to={lp('/contact')} className="fo-btn fo-btn--primary">
                {f.ctaButton} <IconArrowRight size={18} stroke={2} aria-hidden="true" />
              </Link>
              <a
                href={whatsappLink(t.whatsapp.quoteMessage)}
                className="fo-btn fo-btn--ghost"
                target="_blank"
                rel="noopener noreferrer"
              >
                <IconBrandWhatsapp size={18} aria-hidden="true" /> {f.ctaWhatsapp}
              </a>
            </div>
          </div>
        </section>
      )}

      <div className="container fo-main">
        <div className="fo-brand">
          <Link to={lp('/')} className="fo-logo" translate="no" aria-label={t.homeLinkLabel}>
            <img src={logo} alt="" className="fo-logo-image" />
            <span>Spice Yield</span>
          </Link>
          <p className="fo-description">{f.description}</p>
        </div>

        <nav className="fo-col" aria-label={f.products}>
          <h2 className="fo-heading">{f.products}</h2>
          <ul>
            {PRODUCT_ANCHORS.map((id) => (
              <li key={id}><Link to={`${lp('/products')}#${id}`}>{f.productLinks[id]}</Link></li>
            ))}
          </ul>
        </nav>

        <nav className="fo-col" aria-label={f.company}>
          <h2 className="fo-heading">{f.company}</h2>
          <ul>
            {COMPANY_PATHS.map((p) => (
              <li key={p}><Link to={lp(p)}>{t.nav[p]}</Link></li>
            ))}
          </ul>
        </nav>

        <div className="fo-col fo-contact">
          <h2 className="fo-heading">{f.contact}</h2>
          {OFFICES.map((office) => (
            <address className="fo-office" key={office.key}>
              <span className="fo-office-name">
                <span aria-hidden="true">{office.flag}</span> {f[office.key]}
              </span>
              <span>{office.address}</span>
              <a href={`tel:${office.tel}`} className="fo-icon-link">
                <IconPhone size={15} stroke={1.75} aria-hidden="true" /> {office.phone}
              </a>
            </address>
          ))}
          <div className="fo-direct">
            <a href="mailto:hello@spiceyield.com" className="fo-icon-link">
              <IconMail size={15} stroke={1.75} aria-hidden="true" /> hello@spiceyield.com
            </a>
            <a href={whatsappLink(t.whatsapp.defaultMessage)} className="fo-icon-link" target="_blank" rel="noopener noreferrer">
              <IconBrandWhatsapp size={15} stroke={1.75} aria-hidden="true" /> WhatsApp
            </a>
          </div>
        </div>
      </div>

      <div className="container fo-certs">
        <span className="fo-certs-label">{t.certifications.label}</span>
        <ul className="fo-cert-logos">
          {t.certifications.items.filter((c) => certificationLogos[c.name]).map((c) => (
            <li key={c.name}>
              <img src={certificationLogos[c.name]} alt={c.name} title={c.name} loading="lazy" decoding="async" />
            </li>
          ))}
        </ul>
        <ul translate="no">
          {t.certifications.items.filter((c) => !certificationLogos[c.name]).map((c) => <li key={c.name}>{c.name}</li>)}
          <li>{t.certifications.exporter}</li>
        </ul>
      </div>

      <div className="container fo-bottom">
        <p className="fo-copyright">{`© ${new Date().getFullYear()} Spice Yield. ${f.rights}`}</p>
        <div className="fo-bottom-actions">
          <a
            href="https://www.facebook.com/spiceyield"
            className="fo-round"
            aria-label="Facebook"
            target="_blank"
            rel="noopener noreferrer"
          >
            <IconBrandFacebook size={17} stroke={1.75} />
          </a>
          <ThemeToggle theme={theme} toggle={toggleTheme} labels={t} className="theme-toggle fo-round" />
          <Link
            to={localizePath(path, otherLang)}
            className="fo-lang"
            hrefLang={otherLang}
            lang={otherLang}
            translate="no"
          >
            {LANGUAGES[otherLang].label}
          </Link>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
